import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
    ENDPOINTS,
    EXPANDS,
    getUuidV4,
    OdataService,
    OnlineOnlyService,
    UriUtilitiesService,
} from '@evorait/pace-core';
import { catchError, map } from 'rxjs/operators';
import { OdataPIScheduleModel, PhysicalInventoryAlertModel } from '../models/physical-inventory-alert.model';
import { OdataPhysicalInventoryModel, PhysicalInventoryModel } from '../models/physical-inventory.model';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

@Injectable({ providedIn: 'root' })
export class OdataPhysicalInventoryService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly odataService: OdataService,
        private readonly uriUtilities: UriUtilitiesService,
    ) {}

    public getPiSchedule(): Observable<PhysicalInventoryAlertModel | undefined> {
        return this.onlineOnlyService.get(ENDPOINTS.PIScheduleSet).pipe(
            map((response: OdataPIScheduleModel[]) => {
                if (!response || !response[0]) {
                    // if the backend sends an empty response,
                    // then consider there is no PI scheduled currently for the user.
                    return PhysicalInventoryAlertModel.emptyPhysicalInventoryAlertModel();
                }
                return PhysicalInventoryAlertModel.fromODataPIScheduleSet(response[0]);
            }),
            catchError(() => {
                this.log.error(`OdataPhysicalInventoryService: getPiSchedule: Could not get pi schedule set!`);
                // PI schedule fetch failures should not block the user's work.
                return of(undefined);
            }),
        );
    }

    public getPhysicalInventory(): Observable<PhysicalInventoryModel> {
        const query = this.uriUtilities.appendParameter(
            ENDPOINTS.PhysicalInventorySet,
            EXPANDS.ExpandEntityPhysicalInventory,
        );
        return this.onlineOnlyService.get(query).pipe(
            map((response: OdataPhysicalInventoryModel[]) => {
                if (!response) {
                    throw new Error(
                        `OdataPhysicalInventoryService: getPhysicalInventory: Could not get Physical Inventory!`,
                    );
                }
                return PhysicalInventoryModel.fromODataPhysicalInventorySet(response[0]);
            }),
        );
    }

    public sendPhysicalInventory(model: PhysicalInventoryModel): Observable<unknown> {
        return this.odataService.deepInsertRequest(model, ENDPOINTS.PhysicalInventorySet, `${getUuidV4()}`);
    }
}
